import './SequenceContent.scss';

import { Button } from '@hulu/react-style-components';
import { bem } from 'mission-control-common-components';
import React, { useCallback, useState } from 'react';
import type { DropResult } from 'react-beautiful-dnd';
import { DragDropContext } from 'react-beautiful-dnd';

import AdPodPreviewSection from '../AdPodPreviewSection';
import { NEW_SEQUENCE_TITLE, UNSEQUENCED_TABLE_ID } from '../constants';
import { useSequenceContent, useSequenceForm } from '../hooks';
import SequenceTable from '../SequenceTable';
import type { SequenceAdV5, TransformedSequence } from '../types';

const [block, element] = bem('sequence-content');

const SequenceContent = (): React.JSX.Element => {
  const { values, addSequence } = useSequenceForm();
  const { handleDragEnd, filteredUnsequencedAds } = useSequenceContent();

  // While a drag is active each table wrapper hides its overflow so rbd v13
  // resolves #formContainer (rather than the wrapper itself) as the single
  // scroll parent for every Droppable. See SequenceTable.scss for details.
  const [isDragActive, setIsDragActive] = useState(false);

  const onBeforeCapture = useCallback((): void => {
    setIsDragActive(true);
  }, []);

  const onDragEnd = useCallback(
    (dropResult: DropResult): void => {
      setIsDragActive(false);
      handleDragEnd(dropResult);
    },
    [handleDragEnd]
  );

  const getMappedSequenceAds = (sequence: TransformedSequence): SequenceAdV5[] => {
    return sequence?.ads && sequence?.ads.length ? sequence?.ads : [];
  };

  return (
    <DragDropContext onBeforeCapture={onBeforeCapture} onDragEnd={onDragEnd}>
      <div className={block()}>
        {values.sequences.map((sequence, index) => (
          <React.Fragment key={sequence.id}>
            <SequenceTable
              sequence={sequence}
              rows={getMappedSequenceAds(sequence)}
              tableIndex={index}
              isDragActive={isDragActive}
            />
            <AdPodPreviewSection sequence={sequence} sequenceIndex={index} />
          </React.Fragment>
        ))}
        <div>
          <Button
            variant="outlined"
            icon="IconAdd"
            aria-label="add new sequence"
            className={element('add-sequence')}
            onClick={addSequence}
          >
            {NEW_SEQUENCE_TITLE}
          </Button>
        </div>
        <SequenceTable
          rows={filteredUnsequencedAds}
          tableIndex={Number(UNSEQUENCED_TABLE_ID)}
          isDragActive={isDragActive}
        />
      </div>
    </DragDropContext>
  );
};

export default SequenceContent;
